import { postApproveTemporaryAccess } from "api/userAPI";
import Spinner from "components/atoms/loader/Spinner";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MdErrorOutline,
  MdMarkEmailRead,
  MdOutlineAccessTime,
} from "react-icons/md";
import * as s from "./AccountAccessConfirmStyled";
import { CommonResponse } from "api/interfaces/commonInterface.interface";
import BigButton from "components/atoms/buttons/BigButton";
import useStatusMutation from "hook/useStatusMutation";

type ConfirmStatus = "loading" | "success" | "error" | "invalid";

const INITIAL_LOADING_MESSAGE = "Verifying your temporary access request...";
const SUCCESS_MESSAGE =
  "Temporary access approved. You will be redirected to myDW automatically.";
const INVALID_TOKEN_MESSAGE =
  "This approval link is invalid or has already expired. Please request a new link.";
const MAIN_URL = "/";

export default function AccountAccessConfirm(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<ConfirmStatus>("loading");
  const [message, setMessage] = useState(INITIAL_LOADING_MESSAGE);
  const [secondsLeft, setSecondsLeft] = useState(5);

  const mutationApproveTmpAccess = useStatusMutation(
    (approvalToken: string) => postApproveTemporaryAccess(approvalToken),
    {
      onSuccess: (data: CommonResponse) => {
        if (data.error === 0) {
          setStatus("success");
          setMessage(SUCCESS_MESSAGE);
          return;
        }

        setStatus("error");
        setMessage(data.errorString || "Unable to approve temporary access.");
      },
      onError: (error: any) => {
        setStatus("error");
        setMessage(
          error?.response?.data?.errorString ||
            error?.message ||
            "A server error occurred while approving access."
        );
      },
    }
  );

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setMessage(INVALID_TOKEN_MESSAGE);
      return;
    }
    if (!mutationApproveTmpAccess.idle) return;

    setStatus("loading");
    setMessage(INITIAL_LOADING_MESSAGE);
    mutationApproveTmpAccess.mutate(token);
  }, [token]);

  useEffect(() => {
    if (status !== "success") {
      setSecondsLeft(5);
      return;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          window.location.assign(MAIN_URL);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [status]);

  const statusTitle = useMemo(() => {
    if (status === "loading") return "Approving Request";
    if (status === "success") return "Access Granted";
    if (status === "invalid") return "Link Invalid";
    return "Approval Failed";
  }, [status]);

  const icon = useMemo(() => {
    if (status === "loading") return <MdOutlineAccessTime />;
    if (status === "success") return <MdMarkEmailRead />;
    return <MdErrorOutline />;
  }, [status]);

  const onClickRetry = () => {
    if (token) {
      setStatus("loading");
      setMessage(INITIAL_LOADING_MESSAGE);
      mutationApproveTmpAccess.mutate(token);
    }
  };

  return (
    <s.Page>
      <s.BackgroundDecor />
      <s.Card>
        <s.Header>
          <s.Title>Temporary Access Confirmation</s.Title>
          <s.Subtitle>
            We are validating your email approval and applying access
            permission.
          </s.Subtitle>
        </s.Header>

        <s.StatusPanel status={status}>
          <s.StatusMainRow>
            <s.StatusIcon status={status}>{icon}</s.StatusIcon>
            <s.StatusTextGroup>
              <s.StatusTitle>
                {statusTitle}
                {status === "loading" && (
                  <s.LoaderRow>
                    <Spinner size={10} inline />
                  </s.LoaderRow>
                )}
              </s.StatusTitle>
              <s.Message>{message}</s.Message>

              {status === "success" && (
                <s.MetaText>Redirecting to myDW in {secondsLeft}s</s.MetaText>
              )}
            </s.StatusTextGroup>
          </s.StatusMainRow>
        </s.StatusPanel>

        <s.ButtonRow>
          {status === "success" && (
            <BigButton
              label="Go to myDW"
              onClickBtn={() => window.location.assign(MAIN_URL)}
              data-testid="account-access-confirm-button-assign"
            />
          )}

          {(status === "error" || status === "invalid") && (
            <BigButton
              label="Try Again"
              onClickBtn={onClickRetry}
              data-testid="account-access-confirm-button-click-retry"
            />
          )}
        </s.ButtonRow>
      </s.Card>
    </s.Page>
  );
}
